import type { Inventory } from "~/lib/app.types";

export type AvailabilityKind =
  | "inStock"
  | "preOrder"
  | "backOrder"
  | "outOfStock";

export type AvailabilityState = {
  kind: AvailabilityKind;
  inStockDate?: string;
};

const BASE_COPY: Partial<Record<AvailabilityKind, string>> = {
  preOrder: "Pre-Order",
  backOrder: "Back Order",
};

export function deriveAvailabilityState(
  inventory?: Inventory,
): AvailabilityState {
  if (!inventory) return { kind: "outOfStock" };

  const { ats, orderable, preorderable, backorderable, inStockDate } =
    inventory;
  const hasStock = typeof ats === "number" && ats > 0;
  const purchasable = orderable !== false;

  if (!hasStock && preorderable && purchasable) {
    return { kind: "preOrder", inStockDate };
  }
  if (!hasStock && backorderable && purchasable) {
    return { kind: "backOrder", inStockDate };
  }
  if (orderable && hasStock) {
    return { kind: "inStock", inStockDate };
  }
  return { kind: "outOfStock" };
}

/**
 * True only when inventory data is present AND resolves to out-of-stock.
 *
 * Missing inventory returns `false` (treat as available) — `deriveAvailabilityState`
 * maps `undefined` to `outOfStock`, so guarding on `!!inventory` prevents
 * false-flagging products that simply carry no inventory record (or types SCAPI
 * doesn't return inventory for). Pre-order / back-order are purchasable and are
 * therefore NOT out of stock. Used to flag OOS line items in cart + minicart
 * (SFCC-11796).
 */
export function isOutOfStock(inventory?: Inventory): boolean {
  return (
    !!inventory && deriveAvailabilityState(inventory).kind === "outOfStock"
  );
}

export function formatInStockDate(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function getAvailabilityMessage(
  state: AvailabilityState,
): string | null {
  const base = BASE_COPY[state.kind];
  if (!base) return null;
  const formattedDate = formatInStockDate(state.inStockDate);
  return formattedDate ? `${base} — Ships ${formattedDate}` : base;
}

export function getJsonLdAvailability(inventory?: Inventory): string {
  const state = deriveAvailabilityState(inventory);
  if (state.kind === "outOfStock") return "https://schema.org/OutOfStock";
  if (state.kind === "preOrder") return "https://schema.org/PreOrder";
  return "https://schema.org/InStock";
}
